import { useLevelColors } from "@circle-react/components/Leaderboard/colorHelper";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useThemeContext } from "@circle-react/providers/Theme";

import { t } from "@/i18n-js/instance";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { UserImage } from "@/react/components/SettingsApp/CompassCompat/UserImage";

const XLARGE_AVATAR_SIZE = 128;
const LARGE_AVATAR_SIZE = 104;
const MEDIUM_AVATAR_SIZE = 84;
const SMALL_AVATAR_SIZE = 64;
const PROGRESS_STROKE_WIDTH = 4;

type AvatarSize = "xlarge" | "large" | "medium" | "small";

const AVATAR_SIZE_MAP: Record<AvatarSize, number> = {
  xlarge: XLARGE_AVATAR_SIZE,
  large: LARGE_AVATAR_SIZE,
  medium: MEDIUM_AVATAR_SIZE,
  small: SMALL_AVATAR_SIZE,
};

const USER_IMAGE_SIZE_MAP: Record<AvatarSize, "24" | "16" | "14"> = {
  xlarge: "24",
  large: "24",
  medium: "16",
  small: "14",
};

const LEVEL_BADGE_SIZE_MAP: Record<AvatarSize, string> = {
  xlarge: "h-9 w-9",
  large: "h-8 w-8",
  medium: "h-7 w-7",
  small: "h-6 w-6",
};

export const AvatarWithProgress = ({
  avatarUrl,
  name,
  progress = 0,
  level,
  size = "large",
}: {
  avatarUrl?: string;
  name: string;
  progress?: number;
  level?: number;
  size?: AvatarSize;
}) => {
  const { isDarkAppearance } = useThemeContext();
  const BACKGROUND_COLOR = isDarkAppearance ? "#42464D" : "#F0F3F5";
  const AVATAR_SIZE = AVATAR_SIZE_MAP[size];
  const radius = (AVATAR_SIZE - PROGRESS_STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;
  const progressOffset = circumference - (progress / 100) * circumference;
  const levelColors = useLevelColors(level || 1);

  return (
    <div
      className="relative flex items-center justify-center rounded-full"
      style={{ height: AVATAR_SIZE, width: AVATAR_SIZE }}
    >
      <progress
        value={progress}
        max={100}
        aria-label={t("leaderboard.gamification_level_progress")}
        aria-valuenow={progress}
        className="sr-only"
      />
      <svg
        className="absolute"
        width={AVATAR_SIZE}
        height={AVATAR_SIZE}
        style={{ transform: "rotate(-90deg)" }}
        aria-hidden
      >
        <circle
          cx={AVATAR_SIZE / 2}
          cy={AVATAR_SIZE / 2}
          r={radius}
          stroke={BACKGROUND_COLOR}
          strokeWidth={PROGRESS_STROKE_WIDTH}
          fill="none"
        />
        <circle
          cx={AVATAR_SIZE / 2}
          cy={AVATAR_SIZE / 2}
          r={radius}
          stroke={levelColors.progress}
          strokeWidth={PROGRESS_STROKE_WIDTH}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={progressOffset}
          strokeLinecap="round"
        />
      </svg>
      <div
        className="absolute z-0 flex items-center justify-center overflow-hidden rounded-full bg-secondary [&>*]:!h-full [&>*]:!w-full"
        style={{
          height: AVATAR_SIZE - 2 * PROGRESS_STROKE_WIDTH,
          width: AVATAR_SIZE - 2 * PROGRESS_STROKE_WIDTH,
        }}
      >
        <UserImage
          isFullRounded
          src={avatarUrl}
          name={name}
          size={USER_IMAGE_SIZE_MAP[size]}
        />
      </div>
      {level && (
        <div
          className={classNames(
            "absolute -bottom-1 -right-1 z-10 flex items-center justify-center rounded-full",
            LEVEL_BADGE_SIZE_MAP[size],
            levelColors.bg,
          )}
        >
          <Typography.LabelSm weight="bold">
            <span className={levelColors.text}>{level}</span>
          </Typography.LabelSm>
        </div>
      )}
    </div>
  );
};
